// web_components/WebComponent_Utils.ts

// Load all statically written CSS modules
// @ts-ignore
import * as styleModules from './styles';
import { GTM_createClickEvent } from '../tracking_scripts/tag_manager/events';

/**
 * Utility class for handling web component operations such as visibility settings and image initialization.
 */
export class WebComponent_Utils {
	/**
	 * Merges multiple settings objects into one. Later settings objects overwrite earlier ones.
	 * This is useful for combining different configuration sources where more specific settings
	 * should override more general ones.
	 *
	 * @param {Record<string, boolean>[]} settingsArray - An array of settings objects to merge.
	 * Each object represents a set of visibility settings where the key is the element name and
	 * the value is a boolean indicating whether the element should be visible.
	 *
	 * @returns {Record<string, boolean>} - The merged settings object, containing all settings from
	 * the input objects, with later settings overwriting earlier ones when conflicts arise.
	 */
	static mergeSettings(...settingsArray: Record<string, boolean>[]): Record<string, boolean> {
		return settingsArray.reduce((acc, settings) => ({ ...acc, ...settings }), {});
	}

	/**
	 * Applies visibility settings to all elements within a web component's shadow DOM.
	 * This function checks for 'class' elements, <img> elements, and other custom elements to determine their visibility
	 * based on assigned content, attributes, or visibility settings.
	 *
	 * @param {ShadowRoot} shadow - The shadow DOM of the component where visibility settings will be applied.
	 * @param {Record<string, boolean>} [visibilitySettings] - An optional object representing visibility settings for elements.
	 * The key is the element class and the value is a boolean indicating whether the element should be visible.
	 */
	static applyVisibilitySettings(
		shadow: ShadowRoot,
		visibilitySettings?: Record<string, boolean>,
	): void {
		// Get all elements with a 'class' attribute in the shadow DOM
		const elements = shadow.querySelectorAll('[class]');

		elements.forEach((element) => {
			const domElement = element as HTMLElement;
			const className = domElement.getAttribute('class');
			if (!className) return;

			// Determine whether to hide the element
			const shouldHide = this.shouldHideElement(domElement, className, visibilitySettings);

			// Hide the element or its parent based on the element type
			this.toggleElementVisibility(domElement, shouldHide);
		});
	}

	/**
	 * Determines whether an element should be hidden based on its type and visibility settings.
	 * This can be used to conditionally hide <img> elements without a 'src' or 'alt' attribute,
	 * tags without content, <select> elements without options, or any other
	 * element that should be hidden based on the visibility settings.
	 *
	 * @param {HTMLElement} element - The DOM element being evaluated.
	 * @param {string} className - The 'class' attribute of the element.
	 * @param {Record<string, boolean>} [visibilitySettings] - The visibility settings, if any.
	 * @returns {boolean} - Whether the element should be hidden.
	 */
	private static shouldHideElement(
		element: HTMLElement,
		className: string,
		visibilitySettings?: Record<string, boolean>,
	): boolean {
		// Check <img> elements: hide if 'src' or 'alt' attributes are missing
		if (element instanceof HTMLImageElement) {
			return (
				!element.getAttribute('src') ||
				!element.getAttribute('alt') ||
				!this.isVisibleInSettings(className, visibilitySettings)
			);
		}

		// Check <select> elements: hide if there are no options
		if (element instanceof HTMLSelectElement) {
			return (
				element.options.length === 0 ||
				!this.isVisibleInSettings(className, visibilitySettings)
			);
		}

		// Check <input>, <textarea>, and self-closing elements but ensure they're evaluated based on visibility settings
		const selfClosingOrIntentionallyEmptyTags = [
			'input',
			'textarea',
			'br',
			'hr',
			'img',
			'link',
			'meta',
			'i',
		];
		if (selfClosingOrIntentionallyEmptyTags.includes(element.tagName.toLowerCase())) {
			return !this.isVisibleInSettings(className, visibilitySettings);
		}

		// For all other elements, hide based on content or visibility settings
		return (
			((element.textContent?.trim().length ?? 0) === 0 && element.children.length === 0) ||
			!this.isVisibleInSettings(className, visibilitySettings)
		);
	}

	/**
	 * Checks whether an element should be visible based on provided visibility settings.
	 * By default, elements are visible unless explicitly marked as hidden in the settings.
	 *
	 * @param {string} name - The name of the element.
	 * @param {Record<string, boolean>} [visibilitySettings] - The visibility settings, if any.
	 * @returns {boolean} - Whether the element should be visible.
	 */
	private static isVisibleInSettings(
		name: string,
		visibilitySettings?: Record<string, boolean>,
	): boolean {
		// Elements are visible by default unless explicitly set to false in the visibility settings
		return visibilitySettings ? visibilitySettings[name] !== false : true;
	}

	/**
	 * Toggles the visibility of the element or its parent, depending on the element type.
	 * For `<img>` elements, the parent element is hidden when the element should be hidden.
	 * For other elements, the element itself is hidden.
	 *
	 * @param {HTMLElement} element - The DOM element being evaluated. This could be an <img>, <slot>, or other element.
	 * @param {boolean} shouldHide - A boolean indicating whether the element (or its parent, in the case of <img> or <slot>) should be hidden.
	 */
	private static toggleElementVisibility(element: HTMLElement, shouldHide: boolean): void {
		if (element.tagName.toLowerCase() === 'img') {
			const parentElement = element.parentElement as HTMLElement;
			if (parentElement) {
				parentElement.hidden = shouldHide;
			}
		} else {
			element.hidden = shouldHide;
		}
	}

	/**
	 * Ensures the custom element is defined before performing any operations.
	 * This utility function simplifies the usage of `customElements.whenDefined()` and makes sure
	 * that any operations depending on the element being fully defined are executed safely.
	 *
	 * @param {HTMLElement} element - The custom element instance to ensure is defined.
	 * @param {() => void} callback - The callback function to execute once the element is defined.
	 */
	static whenElementDefined(element: HTMLElement, callback: () => void): void {
		customElements.whenDefined(element.localName).then(callback);
	}

	/**
	 * Generates a unique identifier string suitable for use in HTML element IDs or data attributes.
	 * The identifier is prefixed with 'id_' and composed of random alphanumeric characters.
	 *
	 * @returns {string} A unique identifier string, e.g., 'id_k8d93lwzq'.
	 */
	static generateUniqueId(): string {
		return 'id_' + Math.random().toString(36).slice(2, 11);
	}

	/**
	 * Safely defines a custom element and ensures it is redefined if Safari restores the page
	 * from the back-forward cache (bfcache), which may cause the component to become undefined.
	 *
	 * @param {string} tagName - The custom element tag name (e.g., 'wc-banner').
	 * @param {CustomElementConstructor} componentClass - The component class to register.
	 */
	static defineComponent(tagName: string, componentClass: CustomElementConstructor): void {
		const register = () => {
			if (!customElements.get(tagName)) {
				customElements.define(tagName, componentClass);
			}
		};

		register();

		window.addEventListener('pageshow', (event) => {
			if (event.persisted) {
				register();
			}
		});
	}

	/**
	 * Returns <style> elements for a web component’s styles.
	 * Loads styles from a statically bundled map using Webpack's eager context import.
	 *
	 * @param {string} componentPath - The folder + file name, e.g., 'wc-banner' or 'modals/wc-consent-modal'.
	 * @returns {HTMLStyleElement[]} - An array of style elements for base and component styles.
	 * @throws Will throw an error if the base or component CSS module is missing.
	 */
	static createStyleTags(componentPath: string): HTMLStyleElement[] {
		const baseCssName = 'web-component-base';
		const baseModule = styleModules[baseCssName];
		const componentModule = styleModules[componentPath];

		if (!baseModule) {
			throw new Error(`[createStyleTags] Missing base style module: '${baseCssName}'`);
		}

		if (!componentModule) {
			throw new Error(`[createStyleTags] Missing component style module: '${componentPath}'`);
		}

		const baseStyle = document.createElement('style');
		baseStyle.textContent = baseModule.css;
		const componentStyle = document.createElement('style');
		componentStyle.textContent = componentModule.css;
		return [baseStyle, componentStyle];
	}

	/**
	 * Adds tracked click listeners to one or more anchor elements within a Web Component’s shadow DOM.
	 * Will attach listeners to all elements matching the selector, including just one.
	 *
	 * @param {ShadowRoot} shadowRoot - The shadow root of the Web Component.
	 * @param {string} selector - A CSS selector for the anchor element(s).
	 * @param {string} componentName - The GTM event category (e.g., 'wc-promo').
	 * @param {string} interactionLabel - A label describing the interaction (e.g., 'text link').
	 * @param {boolean} [useTextContent=true] - Whether to extract and track the element’s text content.
	 */
	static addGTMClickListener(
		shadowRoot: ShadowRoot,
		selector: string,
		componentName: string,
		interactionLabel: string,
		useTextContent: boolean = true,
	): void {
		const elements = shadowRoot.querySelectorAll(selector);
		const currentPageURL = window.location.href;

		elements.forEach((element) => {
			const anchor = element as HTMLAnchorElement;
			const href = anchor.getAttribute('href');
			const text = useTextContent ? anchor.textContent?.trim() || '' : '';

			anchor.addEventListener('click', (event: Event) => {
				event.preventDefault();

				GTM_createClickEvent(
					currentPageURL,
					componentName,
					interactionLabel,
					text,
					href,
					text,
					currentPageURL,
				);

				window.location.href = href ?? currentPageURL;
			});
		});
	}
}
